import { Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from '@/features/layout/AppShell';
import { AnonymousOnlyGate, AuthGate } from '@/features/auth/AuthGate';
import { AppGuard } from '@/features/auth/AppGuard';

import { ForgotPasswordScreen } from '@/app/auth/ForgotPassword';
import { SignInScreen } from '@/app/auth/SignIn';
import { SignUpScreen } from '@/app/auth/SignUp';
import { VerifyRequiredScreen } from '@/app/auth/VerifyRequired';

import { InviteAcceptScreen } from '@/app/public/InviteAccept';
import { NotFoundScreen } from '@/app/public/NotFound';
import { PublicInvoiceScreen } from '@/app/public/PublicInvoice';
import { ResetPasswordScreen } from '@/app/public/ResetPassword';
import { VerifyEmailScreen } from '@/app/public/VerifyEmail';

import { DashboardScreen } from '@/app/authenticated/Dashboard';
import { InvoicesListScreen } from '@/app/authenticated/invoices/InvoicesList';
import { InvoiceDetailScreen } from '@/app/authenticated/invoices/InvoiceDetail';
import { InvoiceCreateScreen } from '@/app/authenticated/invoices/InvoiceCreate';
import { InvoiceEditScreen } from '@/app/authenticated/invoices/InvoiceEdit';
import { InvoicePreviewScreen } from '@/app/authenticated/invoices/InvoicePreview';
import { InvoiceSentScreen } from '@/app/authenticated/invoices/InvoiceSent';
import { ClientsListScreen } from '@/app/authenticated/clients/ClientsList';
import { ClientDetailScreen } from '@/app/authenticated/clients/ClientDetail';
import { ClientCreateScreen } from '@/app/authenticated/clients/ClientCreate';
import { ClientEditScreen } from '@/app/authenticated/clients/ClientEdit';
import { ServicesListScreen } from '@/app/authenticated/services/ServicesList';
import { TaxesListScreen } from '@/app/authenticated/taxes/TaxesList';
import { AiHistoryScreen } from '@/app/authenticated/ai/AiHistory';
import { NotificationsScreen } from '@/app/authenticated/notifications/NotificationsScreen';
import { AccountSettingsScreen } from '@/app/authenticated/settings/AccountSettings';
import { ChangePasswordScreen } from '@/app/authenticated/settings/ChangePassword';
import { DeleteAccountScreen } from '@/app/authenticated/settings/DeleteAccount';
import { BusinessesListScreen } from '@/app/authenticated/settings/BusinessesList';
import { BusinessesArchivedScreen } from '@/app/authenticated/settings/BusinessesArchived';
import { BusinessCreateScreen } from '@/app/authenticated/settings/BusinessCreate';
import { BusinessDetailScreen } from '@/app/authenticated/settings/BusinessDetail';
import { TeamSettingsScreen } from '@/app/authenticated/settings/Team';
import { DefaultsScreen } from '@/app/authenticated/settings/Defaults';
import { PaymentsSettingsScreen } from '@/app/authenticated/settings/Payments';
import { DesignScreen } from '@/app/authenticated/settings/Design';
import { AppThemeScreen } from '@/app/authenticated/settings/AppTheme';
import { SubscriptionScreen } from '@/app/authenticated/settings/Subscription';
import { FeedbackScreen } from '@/app/authenticated/settings/Feedback';
import { SupportScreen } from '@/app/authenticated/settings/Support';
import { DevToolsScreen } from '@/app/authenticated/settings/DevTools';
import { OnboardingCreateBusinessScreen } from '@/app/authenticated/onboarding/CreateBusiness';

/**
 * Single source of truth for the web app's URL contracts.
 *
 * Three top-level groups:
 *   - PUBLIC — anyone can hit them, no auth required. Their URLs
 *     are baked into emails the server generates, so the paths
 *     here are part of the wire contract (`/i/:token`,
 *     `/auth/verify-email`, `/auth/reset-password`,
 *     `/invites/accept/:token`).
 *   - ANON-ONLY — sign-in / sign-up / forgot-password. Authenticated
 *     users get bounced back into the app.
 *   - AUTHENTICATED — wrapped in `AuthGate` + `AppGuard` and rendered
 *     inside the `AppShell` (sidebar + topbar). The guard handles
 *     org reconciliation and email-verified routing.
 */
export function AppRouter() {
  return (
    <Routes>
      {/* Public, no auth */}
      <Route path="/i/:token" element={<PublicInvoiceScreen />} />
      <Route path="/auth/verify-email" element={<VerifyEmailScreen />} />
      <Route path="/auth/reset-password" element={<ResetPasswordScreen />} />
      <Route path="/invites/accept/:token" element={<InviteAcceptScreen />} />

      {/* Anonymous-only */}
      <Route
        path="/sign-in"
        element={
          <AnonymousOnlyGate>
            <SignInScreen />
          </AnonymousOnlyGate>
        }
      />
      <Route
        path="/sign-up"
        element={
          <AnonymousOnlyGate>
            <SignUpScreen />
          </AnonymousOnlyGate>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <AnonymousOnlyGate>
            <ForgotPasswordScreen />
          </AnonymousOnlyGate>
        }
      />

      {/* Authenticated, but rendered without the shell */}
      <Route
        path="/verify-required"
        element={
          <AuthGate>
            <VerifyRequiredScreen />
          </AuthGate>
        }
      />

      {/* Authenticated app shell */}
      <Route
        path="/*"
        element={
          <AuthGate>
            <AppGuard>
              <AppShell>
                <Routes>
                  <Route index element={<DashboardScreen />} />

                  <Route path="invoices" element={<InvoicesListScreen />} />
                  <Route path="invoices/new" element={<InvoiceCreateScreen />} />
                  <Route path="invoices/new/ai" element={<InvoiceCreateScreen />} />
                  <Route path="invoices/:invoiceId" element={<InvoiceDetailScreen />} />
                  <Route path="invoices/:invoiceId/edit" element={<InvoiceEditScreen />} />
                  <Route path="invoices/:invoiceId/preview" element={<InvoicePreviewScreen />} />
                  <Route path="invoices/sent" element={<InvoiceSentScreen />} />

                  <Route path="clients" element={<ClientsListScreen />} />
                  <Route path="clients/new" element={<ClientCreateScreen />} />
                  <Route path="clients/:clientId" element={<ClientDetailScreen />} />
                  <Route path="clients/:clientId/edit" element={<ClientEditScreen />} />

                  <Route path="services" element={<ServicesListScreen />} />
                  <Route path="taxes" element={<TaxesListScreen />} />

                  <Route path="ai" element={<AiHistoryScreen />} />
                  <Route path="ai/:conversationId" element={<AiHistoryScreen />} />

                  <Route path="notifications" element={<NotificationsScreen />} />

                  <Route path="settings" element={<Navigate to="/settings/account" replace />} />
                  <Route path="settings/account" element={<AccountSettingsScreen />} />
                  <Route path="settings/change-password" element={<ChangePasswordScreen />} />
                  <Route path="settings/delete-account" element={<DeleteAccountScreen />} />
                  <Route path="settings/businesses" element={<BusinessesListScreen />} />
                  <Route path="settings/businesses/archived" element={<BusinessesArchivedScreen />} />
                  <Route path="settings/businesses/new" element={<BusinessCreateScreen />} />
                  <Route path="settings/businesses/:orgId" element={<BusinessDetailScreen />} />
                  <Route path="settings/team" element={<TeamSettingsScreen />} />
                  <Route path="settings/defaults" element={<DefaultsScreen />} />
                  <Route path="settings/payments" element={<PaymentsSettingsScreen />} />
                  <Route path="settings/design" element={<DesignScreen />} />
                  <Route path="settings/theme" element={<AppThemeScreen />} />
                  <Route path="settings/subscription" element={<SubscriptionScreen />} />
                  <Route path="settings/feedback" element={<FeedbackScreen />} />
                  <Route path="settings/support" element={<SupportScreen />} />
                  <Route path="settings/dev-tools" element={<DevToolsScreen />} />

                  <Route path="onboarding/create-business" element={<OnboardingCreateBusinessScreen />} />

                  <Route path="*" element={<NotFoundScreen />} />
                </Routes>
              </AppShell>
            </AppGuard>
          </AuthGate>
        }
      />
    </Routes>
  );
}
